import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { Truck, MapPin, Clock, Navigation, Loader2, Map as MapIcon } from 'lucide-react';
import { getVehicleLocations } from '../../services/locationService';
import { supabase } from '../../lib/supabase';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface TruckData {
  truckNumber: string;
  latitude: number;
  longitude: number;
  speed: number;
  heading?: number;
  lastUpdate: Date;
  address?: string;
  city?: string;
  state?: string;
}

interface ActiveLoad {
  id: string;
  dispatchNumber: string;
  pickupCity: string;
  pickupState: string;
  deliveryCity: string;
  deliveryState: string;
  pickupTime: string;
  deliveryTime: string;
  status: 'active' | 'planned';
  distanceToPickup?: number;
  distanceToDelivery?: number;
  etaToPickup?: string;
  etaToDelivery?: string;
  isLate?: boolean;
}

interface TruckTrackingPageProps {
  truckId: string;
}

// Component to prevent map from re-centering on updates
function MapUpdater({ truckData, markerRef }: { truckData: TruckData | null; markerRef: React.MutableRefObject<L.Marker | null> }) {
  const map = useMap();
  const prevPosition = useRef<[number, number] | null>(null);
  
  useEffect(() => {
    if (truckData && markerRef.current) {
      const newPosition: [number, number] = [truckData.latitude, truckData.longitude];
      
      // If we have a previous position, animate to new position
      if (prevPosition.current) {
        const [oldLat, oldLng] = prevPosition.current;
        const [newLat, newLng] = newPosition;
        
        // Only animate if position changed
        if (oldLat !== newLat || oldLng !== newLng) {
          animateMarker(markerRef.current, oldLat, oldLng, newLat, newLng, 3000);
        }
      }
      
      prevPosition.current = newPosition;
    }
  }, [truckData, markerRef, map]);
  
  return null;
}

// Smooth marker animation function
function animateMarker(marker: L.Marker, startLat: number, startLng: number, endLat: number, endLng: number, duration: number) {
  const startTime = Date.now();
  const latDiff = endLat - startLat;
  const lngDiff = endLng - startLng;

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Easing function for smooth animation
    const easeProgress = progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;

    const currentLat = startLat + latDiff * easeProgress;
    const currentLng = startLng + lngDiff * easeProgress;

    marker.setLatLng([currentLat, currentLng]);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
}

export function TruckTrackingPage({ truckId }: TruckTrackingPageProps) {
  const [truckData, setTruckData] = useState<TruckData | null>(null);
  // activeLoads kept for future use but not displayed to brokers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeLoads, setActiveLoads] = useState<ActiveLoad[]>([]);
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('Leader Group');
  const [mapType, setMapType] = useState<'street' | 'satellite'>('street');
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    fetchCompanySettings();
    fetchTruckLocation();
    // Auto-refresh every 10 seconds for live updates
    const interval = setInterval(fetchTruckLocation, 10000);
    return () => clearInterval(interval);
  }, [truckId]);

  const fetchCompanySettings = async () => {
    try {
      const { data } = await supabase
        .from('company_settings')
        .select('company_name')
        .limit(1)
        .single();
      
      if (data?.company_name) {
        setCompanyName(data.company_name);
      }
    } catch (err) {
      // Silently fail and use default company name
      console.log('Could not fetch company settings, using default');
    }
  };

  const fetchTruckLocation = async () => {
    try {
      if (!truckId) {
        setError('No truck ID provided');
        setLoading(false);
        return;
      }

      // Get truck info from database
      // Clean up the truck ID - decode URL encoding first
      let cleanTruckId = decodeURIComponent(truckId).trim();
      
      console.log('Original truck ID from URL:', truckId);
      console.log('Decoded truck ID:', cleanTruckId);
      
      // Try multiple formats to find the truck
      // The database stores unit_number as just the number (e.g., "6296")
      // But the URL might have "Truck 6296" or just "6296"
      
      let unitNumber = cleanTruckId;
      
      // Remove "Truck " prefix if present to get just the number
      unitNumber = unitNumber.replace(/^Truck\s+/i, '').trim();
      
      console.log('Looking up truck with unit_number:', unitNumber);
      
      // Query truck by unit_number - use ilike for case-insensitive match
      const { data: trucks, error: truckError } = await supabase
        .from('trucks')
        .select('id, unit_number, samsara_id, samsara_vehicle_id')
        .ilike('unit_number', unitNumber)
        .limit(1);

      console.log('Truck query result:', { trucks, truckError });

      if (truckError) {
        console.error('Truck query error:', truckError);
        setError(`Database error: ${truckError.message}`);
        setLoading(false);
        return;
      }

      if (!trucks || trucks.length === 0) {
        setError('Truck not found');
        setLoading(false);
        return;
      }

      const truck = trucks[0];

      if (!truck.samsara_id) {
        setError('Truck not equipped with GPS');
        setLoading(false);
        return;
      }

      // Get location from Samsara
      const response = await getVehicleLocations();
      
      if (!response.success || !response.locations) {
        throw new Error('Failed to fetch location');
      }

      // Try to find location using either samsara_id or samsara_vehicle_id
      const location = response.locations.find(loc => 
        loc.vehicleId === truck.samsara_id || loc.vehicleId === truck.samsara_vehicle_id
      );
      
      if (!location) {
        setError('Location not available for this truck');
        setLoading(false);
        return;
      }

      // Reverse geocode to get address
      const addressInfo = await reverseGeocode(location.latitude || 0, location.longitude || 0);

      const truckInfo = {
        truckNumber: truck.unit_number || 'Unknown',
        latitude: location.latitude || 0,
        longitude: location.longitude || 0,
        speed: Math.round((location.speed || 0) * 0.621371), // Convert to mph
        heading: location.heading,
        lastUpdate: new Date(location.time),
        address: addressInfo.address,
        city: addressInfo.city,
        state: addressInfo.state,
      };

      setTruckData(truckInfo);

      // Fetch active loads for this truck
      await fetchActiveLoads(truckId, truckInfo.latitude, truckInfo.longitude);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching truck location:', err);
      setError('Failed to load truck location');
      setLoading(false);
    }
  };

  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'TMS-Tracking-App'
          }
        }
      );
      
      const data = await response.json();
      const addr = data.address || {};
      
      // Try multiple address components for city
      const city = addr.city || addr.town || addr.village || addr.county || addr.municipality || 
                   (addr.road ? addr.road.split(' ').slice(0, 2).join(' ') : null);
      
      // Get state abbreviation if available
      const state = addr['ISO3166-2-lvl4']?.split('-')[1] || addr.state;
      
      // Build a better address string
      const addressParts = [];
      if (addr.road) addressParts.push(addr.road);
      if (addr.county && !city) addressParts.push(addr.county);
      const addressStr = addressParts.length > 0 ? addressParts.join(', ') : data.display_name?.split(',').slice(0, 2).join(',');
      
      return {
        city: city || 'Location',
        state: state || '',
        address: addressStr || `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      };
    } catch (error) {
      return {
        city: 'Location',
        state: '',
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      };
    }
  };

  const fetchActiveLoads = async (truckId: string, currentLat: number, currentLng: number) => {
    try {
      // Get truck ID from database first
      let cleanTruckId = decodeURIComponent(truckId).trim();
      let unitNumber = cleanTruckId;
      
      // If it doesn't start with "Truck " or "T-", add "Truck " prefix
      if (!cleanTruckId.match(/^(Truck\s+|T-)/i)) {
        unitNumber = `Truck ${cleanTruckId}`;
      }
      
      const { data: trucks } = await supabase
        .from('trucks')
        .select('id')
        .ilike('unit_number', unitNumber)
        .limit(1);
      
      const truck = trucks && trucks.length > 0 ? trucks[0] : null;

      if (!truck) return;

      // Get active dispatches for this truck
      const { data: dispatches, error } = await supabase
        .from('dispatches')
        .select(`
          id,
          dispatch_number,
          status,
          dispatch_date,
          loads!dispatches_load_id_fkey (
            pickup_city,
            pickup_state,
            pickup_date,
            delivery_city,
            delivery_state,
            delivery_date
          )
        `)
        .eq('truck_id', truck.id)
        .in('status', ['active', 'planned']);

      if (error) throw error;

      const loads: ActiveLoad[] = await Promise.all((dispatches || []).map(async (dispatch: any) => {
        const load = dispatch.loads;
        
        // Geocode pickup and delivery locations
        const pickupCoords = await geocodeLocation(load.pickup_city, load.pickup_state);
        const deliveryCoords = await geocodeLocation(load.delivery_city, load.delivery_state);

        let distanceToPickup = 0;
        let distanceToDelivery = 0;
        let etaToPickup = '';
        let etaToDelivery = '';
        let isLate = false;

        if (pickupCoords) {
          distanceToPickup = calculateDistance(currentLat, currentLng, pickupCoords[0], pickupCoords[1]);
          const hoursToPickup = distanceToPickup / 50; // 50 mph average
          etaToPickup = `${Math.round(hoursToPickup * 60)} min`;
          
          // Check if late for pickup
          const pickupTime = new Date(load.pickup_date);
          const estimatedArrival = new Date(Date.now() + hoursToPickup * 3600000);
          isLate = estimatedArrival > pickupTime;
        }

        if (deliveryCoords) {
          distanceToDelivery = calculateDistance(currentLat, currentLng, deliveryCoords[0], deliveryCoords[1]);
          const hoursToDelivery = distanceToDelivery / 50;
          etaToDelivery = `${Math.round(hoursToDelivery * 60)} min`;
        }

        return {
          id: dispatch.id,
          dispatchNumber: dispatch.dispatch_number,
          pickupCity: load.pickup_city,
          pickupState: load.pickup_state,
          deliveryCity: load.delivery_city,
          deliveryState: load.delivery_state,
          pickupTime: dispatch.pickup_date,
          deliveryTime: dispatch.delivery_date,
          status: dispatch.status,
          distanceToPickup,
          distanceToDelivery,
          etaToPickup,
          etaToDelivery,
          isLate,
        };
      }));

      setActiveLoads(loads);
      
      // Set route coordinates for the first active load
      if (loads.length > 0 && loads[0]) {
        const firstLoad = loads[0];
        const pickupCoords = await geocodeLocation(firstLoad.pickupCity, firstLoad.pickupState);
        const deliveryCoords = await geocodeLocation(firstLoad.deliveryCity, firstLoad.deliveryState);
        
        if (pickupCoords && deliveryCoords) {
          // Get actual road route from pickup to delivery only
          const route = await getRouteBetweenPoints([
            [pickupCoords[1], pickupCoords[0]],
            [deliveryCoords[1], deliveryCoords[0]]
          ]);
          
          if (route) {
            setRouteCoordinates(route);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching active loads:', error);
    }
  };

  const getRouteBetweenPoints = async (coordinates: [number, number][]): Promise<[number, number][] | null> => {
    try {
      // Build OSRM API URL with waypoints
      const coords = coordinates.map(c => `${c[0]},${c[1]}`).join(';');
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`
      );
      
      const data = await response.json();
      
      if (data.routes && data.routes[0]) {
        // Convert GeoJSON coordinates [lng, lat] to Leaflet format [lat, lng]
        return data.routes[0].geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]);
      }
      
      return null;
    } catch (error) {
      // Expected fallback when OSRM is unavailable.
      console.log('Route unavailable:', error);
      return null;
    }
  };

  const geocodeLocation = async (city: string, state: string): Promise<[number, number] | null> => {
    try {
      const query = `${city}, ${state}`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        {
          headers: {
            'User-Agent': 'TMS-Tracking-App'
          }
        }
      );
      
      const data = await response.json();
      if (data && data.length > 0) {
        return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getTimeSinceUpdate = () => {
    if (!truckData) return '';
    
    const now = new Date();
    const diff = now.getTime() - truckData.lastUpdate.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return '0 min ago';
    if (minutes < 60) return `${minutes} min ago`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  };

  // Create custom truck marker
  const createTruckIcon = (truckNumber: string) => {
    return L.divIcon({
      className: 'custom-truck-marker',
      html: `
        <div style="
          background: linear-gradient(135deg, #AC021A 0%, #8A0115 100%);
          color: white;
          padding: 8px 12px;
          border-radius: 8px;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(172, 2, 26, 0.4);
          white-space: nowrap;
          border: 2px solid white;
        ">
          🚛 Truck ${truckNumber}
        </div>
      `,
      iconSize: [120, 40],
      iconAnchor: [60, 40],
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900/20 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#AC021A] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading truck location...</p>
        </div>
      </div>
    );
  }

  if (error || !truckData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900/20 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/90 border border-red-500/30 rounded-lg p-8 max-w-md text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Truck Not Found</h2>
          <p className="text-gray-400">{error || 'Unable to load truck location'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900/20 to-gray-900 p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Truck className="h-8 w-8 text-[#AC021A]" />
            <h1 className="text-3xl font-bold text-white">Live Truck Tracking</h1>
          </div>
          <p className="text-white">Real-time GPS location powered by Samsara</p>
        </div>

        {/* Truck Info Card */}
        <div className="rounded-lg border border-[#AC021A]/30 shadow-sm bg-gray-800/90">
          <div className="flex flex-col space-y-1.5 p-6 bg-gradient-to-r from-[#AC021A]/50 to-[#8A0115]/30 border-b border-[#AC021A]/20">
            <h3 className="text-2xl font-semibold leading-none tracking-tight flex items-center gap-2 text-white">
              <Truck className="h-6 w-6 text-[#AC021A]" />
              Truck {truckData.truckNumber}
            </h3>
          </div>

          <div className="p-6 pt-6 space-y-4">
            {/* Location Info */}
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-[#AC021A]/30 to-[#8A0115]/20 rounded-lg border border-[#AC021A]/30">
              <MapPin className="h-5 w-5 text-[#AC021A] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-300 mb-1">Current Location</p>
                <p className="text-base font-semibold text-white">
                  {truckData.city}{truckData.state && `, ${truckData.state}`}
                </p>
                <p className="text-xs text-gray-400 mt-1">{truckData.address}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {truckData.latitude.toFixed(6)}, {truckData.longitude.toFixed(6)}
                </p>
              </div>
            </div>

            {/* Speed Info */}
            {truckData.speed > 0 && (
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-900/30 to-green-800/20 rounded-lg border border-green-500/30">
                <Navigation className="h-5 w-5 text-green-400" />
                <div>
                  <p className="text-sm font-medium text-green-300">Speed</p>
                  <p className="text-lg font-bold text-white">{truckData.speed} mph</p>
                </div>
              </div>
            )}

            {/* Active Loads section removed - this page is for brokers */}

            {/* Last Update */}
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="h-4 w-4 text-[#AC021A]" />
              <span>
                Last updated: {truckData.lastUpdate.toLocaleString()} ({getTimeSinceUpdate()})
              </span>
            </div>

            {/* Map */}
            <div className="rounded-lg overflow-hidden border border-[#AC021A]/30 relative">
              {/* Map Type Toggle */}
              <div className="absolute top-2 right-2 bg-white rounded-lg shadow-md z-[1000] overflow-hidden">
                <button
                  onClick={() => setMapType('street')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    mapType === 'street'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MapIcon className="w-4 h-4" />
                    <span>Street</span>
                  </div>
                </button>
                <button
                  onClick={() => setMapType('satellite')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${
                    mapType === 'satellite'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MapIcon className="w-4 h-4" />
                    <span>Satellite</span>
                  </div>
                </button>
              </div>
              
              <MapContainer
                center={[truckData.latitude, truckData.longitude]}
                zoom={13}
                style={{ height: '400px', width: '100%' }}
                attributionControl={false}
              >
                {mapType === 'street' ? (
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                ) : (
                  <>
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    />
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    />
                  </>
                )}
                
                {/* Route line if available */}
                {routeCoordinates.length > 0 && (
                  <Polyline
                    positions={routeCoordinates}
                    color="#3B82F6"
                    weight={4}
                    opacity={0.7}
                    dashArray="10, 10"
                  />
                )}
                
                <MapUpdater truckData={truckData} markerRef={markerRef} />
                
                <Marker
                  position={[truckData.latitude, truckData.longitude]}
                  icon={createTruckIcon(truckData.truckNumber)}
                  ref={markerRef}
                >
                  <Popup>
                    <div style={{ textAlign: 'center' }}>
                      <strong>Truck {truckData.truckNumber}</strong><br />
                      <small>{truckData.latitude.toFixed(6)}, {truckData.longitude.toFixed(6)}</small>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>

        {/* Auto-refresh Notice */}
        <div className="rounded-lg border border-[#AC021A]/20 shadow-sm bg-gray-800/50">
          <div className="p-6 pt-4 pb-4">
            <p className="text-xs text-center text-white">
              🔄 Location updates automatically every 10 seconds with live animation
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm">
          <p className="flex items-center justify-center gap-2 text-white">
            <span className="text-[#AC021A]">●</span>
            Powered by {companyName}
            <span className="text-[#AC021A]">●</span>
          </p>
        </div>
      </div>
    </div>
  );
}
